import _ from 'underscore';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import ruLocale from 'date-fns/locale/ru';
import dynamic from 'next/dynamic';
import { differenceInHours, isPast, isFuture, format } from 'date-fns';

import Button from 'components/Button';

import {
	formatTimeFromMilliseconds,
	getDateWithoutTimezone
} from 'utils/helpers/dateTime';
import styles from '../styles.module.scss';

import Divider from './Divider';

const BuyTicketModal = dynamic(
	() => import('components/modals/BuyTicketModal'),
	{
		ssr: false
	}
);

const ShareEventModal = dynamic(
	() => import('components/modals/ShareEventModal'),
	{
		ssr: false
	}
);

const getInitialItemConfig = (seance, isKino, event) => {
	let currentDate = getDateWithoutTimezone();

	const difference = differenceInHours(
		getDateWithoutTimezone(seance.localeEndDate),
		getDateWithoutTimezone(seance.localeStartDate)
	);

	const isLongPeriod =
		difference >= 24 &&
		isPast(getDateWithoutTimezone(seance.localeStartDate)) &&
		isFuture(getDateWithoutTimezone(seance.localeEndDate));

	if (!isKino || !isLongPeriod) {
		currentDate = getDateWithoutTimezone(seance.localeStartDate);
	}

	const freeTicket = () => {
		const findFree = _(event.tags).findWhere({ name: 'besplatno' });
		return !!(findFree || !event?.price || !event?.price?.max);
	};

	const showTime =
		event.roundTheClock ||
		(!(isKino && difference >= 24) && !!seance.startTime && !!seance.endTime);

	return {
		currentDate,
		freeTicket,
		showTime
	};
};

const PlaceItem = ({ placeElement, event, isKino, showShareModal = false }) => {
	const { address, eipskId, saleLink, title, seances } = placeElement;

	const [isSaleModalOpen, setIsSaleModalOpen] = useState(false);
	const [isShareModalOpen, setIsShareModalOpen] = useState(false);

	const toggleSaleModalStatus = () => setIsSaleModalOpen((prev) => !prev);
	const toggleShareModalStatus = () => setIsShareModalOpen((prev) => !prev);

	const shortSaleLink = saleLink?.match(/\/\/([\s\S]+?)\//)?.[1] || saleLink;

	return (
		<div key={eipskId} className={styles.section}>
			<div className={styles.place}>
				{title && <div className={styles.placeName}>{title}</div>}
				{address && <div className={styles.placeAddress}>{address}</div>}
			</div>

			{seances.map((seance) => {
				const { startTime, endTime, localeStartDate, localeEndDate } = seance;
				const { currentDate, freeTicket, showTime } = getInitialItemConfig(
					seance,
					isKino,
					event
				);

				return (
					<div
						key={format(currentDate, 'yyyy-MM-dd:HH-mm')}
						className={styles.content}
					>
						<div className={styles.contentDate}>
							<div className={styles.contentDateNumber}>
								{currentDate.getDate()}
							</div>
							<div className={styles.contentDateInner}>
								<div className={styles.contentDateWeekDay}>
									{format(getDateWithoutTimezone(localeStartDate), 'EEEE', {
										locale: ruLocale
									})}
								</div>
								<div className={styles.contentDateMonth}>
									{format(getDateWithoutTimezone(localeEndDate), 'LLLL', {
										locale: ruLocale
									})}
								</div>
							</div>
						</div>
						<Divider width="0 32px" margin="0 32px" />

						{showTime && (
							<>
								<div className={styles.contentTime}>
									{event.roundTheClock
										? 'Круглосуточно'
										: `${formatTimeFromMilliseconds(
												startTime
										  )} - ${formatTimeFromMilliseconds(endTime)}`}
								</div>
								<Divider width="0 32px" margin="0 32px" />
							</>
						)}

						{freeTicket() ? (
							<div className={styles.contentPrice}>Бесплатно</div>
						) : (
							<div className={styles.contentPrice}>
								от {event?.price?.min} руб
							</div>
						)}
						{!freeTicket() && saleLink && !showShareModal && (
							<Button
								className={styles.ScheduleButton}
								icon="ticket"
								onClick={toggleSaleModalStatus}
								theme="adaptiveGeometry"
								title="Перейти к покупке"
							/>
						)}
						{showShareModal && (
							<Button
								className={styles.ScheduleButton}
								icon="share"
								onClick={toggleShareModalStatus}
								theme="adaptiveGeometry"
								title="Поделиться"
							/>
						)}
						{isShareModalOpen && (
							<ShareEventModal
								isModalShown={isShareModalOpen}
								hideModal={toggleShareModalStatus}
								{...{ event, seance }}
							/>
						)}
					</div>
				);
			})}
			{isSaleModalOpen && (
				<BuyTicketModal
					isShowBuyTicketModal={isSaleModalOpen}
					hideBuyTicketModal={toggleSaleModalStatus}
				>
					<a
						href={saleLink}
						target="_blank"
						rel="noopener"
						className={styles.link}
					>
						{shortSaleLink || 'Перейти к билету'}
					</a>
				</BuyTicketModal>
			)}
		</div>
	);
};

PlaceItem.propTypes = {
	placeElement: PropTypes.shape({
		address: PropTypes.string,
		eipskId: PropTypes.number,
		saleLink: PropTypes.string,
		seances: PropTypes.arrayOf(
			PropTypes.shape({
				startDate: PropTypes.string,
				endDate: PropTypes.string,
				startTime: PropTypes.number,
				endTime: PropTypes.number
			})
		),
		title: PropTypes.string
	}),
	event: PropTypes.shape({
		tags: PropTypes.array,
		price: PropTypes.object,
		roundTheClock: PropTypes.bool
	}),
	isKino: PropTypes.bool,
	showShareModal: PropTypes.bool
};

PlaceItem.defaultProps = {
	placeElement: {
		address: null,
		eipskId: null,
		saleLink: null,
		seances: [],
		title: null
	},
	event: {
		price: {},
		roundTheClock: false,
		tags: []
	}
};

export default PlaceItem;
